import { useEffect, useState, useRef } from "react";
import apiService from "../../../services/service";
import API_ROUTES from "../../../api-routes";
import { useParams } from "react-router-dom";
import z from "zod";
import useAttachmentFilter from "~/hooks/useAttachmentFilter";

function useRequestAttachment() {
  const [attachment, setAttachment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState(10);
  const [page, setPage] = useState(0);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [first, setFirst] = useState(0);
  const [attachmentConfirmModal, setAttachmentConfirmModal] = useState(false);
  const [temporaryMessage, setTemporaryMessage] = useState("");
  const [isAttachmentLoader, setIsAttachmentLoader] = useState(false);
  const [selectedFile, setSelectedFile] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [restoreMessage, setRstoreMessage] = useState("");
  const [requestdata, setRequestData] = useState({});
  const [requestLoading, setReuestLoading] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [chipdata, setChipData] = useState({});
  const removeFnRef = useRef(null);
  const { id } = useParams();
  const {
    data,
    loading: filterLoading,
    error,
    onFilterChange,
    onFilterApply,
    onSortOrderChange,
    allData,
    onPageChange: filterPageChange,
    currentPage,
  } = useAttachmentFilter();
  const handleRemoveFilter = (id) => {
    if (removeFnRef.current) {
      const { value: updatedValue, grouped } = removeFnRef.current(id);
      setFilterData(updatedValue);
      handleFilterChange(grouped);
    }
  };
  const handleFilterChange = (data) => {
    const categories = Object.entries(data?.category || {}).map(
      ([key, value]) => ({
        category: key,
        subCategories: value?.map((sub) =>
          sub.includes("/") ? sub.split("/").pop() : sub,
        ),
      }),
    );
    setChipData(categories);
    onFilterChange({
      categories,
      typeId: id,
      type: "AD_HOC",
    });
  };
  const selectedItems = Object.values(filterData || {}).filter(
    (value) =>
      !["category", "service_line", "entities", "year"].includes(value?.id),
  );
  useEffect(() => {
    if (selectedItems?.length) {
      onFilterApply();
    } else {
      fetchAttachment();
    }
  }, [filterData]);
  const onPageChange = (event) => {
    setFirst(event.first);
    setRows(event.rows);
  };
  const fetchRequest = async () => {
    setReuestLoading(true);
    const { data } = await apiService(API_ROUTES.FETCH_REQUEST, "GET", {
      replace: {
        id: id,
      },
      schema: z.object({
        id: z.string().uuid(),
      }),
    });

    setReuestLoading(false);
    setRequestData(data);
  };
  const fetchAttachment = async (skipCache = false) => {
    setLoading(true);
    setIsDataAvailable(true);
    const { data } = await apiService(API_ROUTES.FETCH_DOCUMENTS, "GET", {
      replace: { id, type: "AD_HOC" },
      skipCache,
    });
    if (data?.length) {
      setAttachment(data);
      setIsDataAvailable(true);
    } else {
      setAttachment([]);
      setIsDataAvailable(false);
    }
    setLoading(false);
  };
  useEffect(() => {
    fetchAttachment();
    fetchRequest();
  }, []);

  const handleDeleteDocument = (file) => {
    setAttachmentConfirmModal(true);
    setSelectedFile(file);
  };
  const handleConfirmDelete = async () => {
    setIsAttachmentLoader(true);
    setRstoreMessage("");
    setTemporaryMessage("");
    const { status } = await apiService(
      API_ROUTES.DELETE_ATTACHMENT,
      "DELETE",
      {
        body: { blobUrl: selectedFile?.blob_url, name: selectedFile?.name },
        replace: {
          relatedId: id,
          relatedType: "REQUEST",
        },
      },
    );
    if (status) {
      setTimeout(async () => {
        setLoading(true);
        if (selectedItems?.length) {
          await onFilterApply();
        } else {
          await fetchAttachment(true); // GXM-1769: skipCache after delete
        }
        setAttachmentConfirmModal(false);
        setTemporaryMessage(
          `${selectedFile?.displayName || selectedFile?.name} has been deleted.`,
        );
        setIsAttachmentLoader(false);
      }, 2000);
    }
  };

  const handleRestoreAttachment = async () => {
    setTemporaryMessage("");
    setRstoreMessage("");
    const { status } = await apiService(API_ROUTES.RESTORE_ATTACHMENT, "POST", {
      body: { blobUrl: selectedFile?.blob_url, name: selectedFile?.name },
    });
    if (status) {
      setTimeout(async () => {
        if (selectedItems?.length) {
          await onFilterApply();
        } else {
          await fetchAttachment(true); // GXM-1769: skipCache after restore
        }
        setRstoreMessage(
          `${selectedFile?.displayName || selectedFile?.name} has been restored.`,
        );
      }, 2000);
    }
  };

  return {
    attachment,
    loading,
    rows,
    onPageChange,
    isDataAvailable,
    first,
    handleDeleteDocument,
    attachmentConfirmModal,
    setAttachmentConfirmModal,
    handleConfirmDelete,
    handleRestoreAttachment,
    temporaryMessage,
    isAttachmentLoader,
    successMessage,
    setSuccessMessage,
    restoreMessage,
    setRstoreMessage,
    setTemporaryMessage,
    requestdata,
    setRequestData,
    requestLoading,
    setReuestLoading,
    onFilterApply,
    setFilterData,
    handleFilterChange,
    handleRemoveFilter,
    filterData,
    removeFnRef,
    selectedItems,
    data,
    filterLoading,
    error,
    onFilterChange,
    onFilterApply,
    onSortOrderChange,
    allData,
    filterPageChange,
    currentPage,
    page,
    fetchAttachment,
  };
}

export default useRequestAttachment;
